import { z } from "zod";

/**
 * SelectOption schema for form selections
 * @deprecated Use employeeValueSchema for author/signer selections
 */
export const selectOptionSchema = z.object({
	id: z.string(),
	label: z.string(),
});

/**
 * Employee value schema for employee selections (authors, signers, etc.)
 * Supports both actual employees and static relational options like "Employee's Manager"
 */
export const employeeValueSchema = z.object({
	id: z.string(),
	name: z.string(),
});

/**
 * Schema for Standard Review Form
 * Used in manual review cycles and template-based cycles
 *
 * Note: When isRelativeDates is true, date fields should contain numbers (days).
 * When false, date fields should contain Date objects.
 * Due date fields are optional and only required when corresponding authors/signers are selected.
 */
export const standardFormSchema = z
	.object({
		formType: z.literal("standard"),
		formName: z.string().min(1, "Form name is required").max(250, "Form name cannot exceed 250 characters"),
		formAuthors: z.array(employeeValueSchema).min(1, "This is a required field"),
		formSigners: z.array(employeeValueSchema),
		authorsDueDate: z.union([z.date(), z.number()]).optional(),
		signersDueDate: z.union([z.date(), z.number()]).optional(),
		isRelativeDates: z.boolean().default(false),
		makeVisibleWhenCompleted: z.boolean().default(false),
		redactAuthors: z.boolean().default(false),
	})
	.refine(
		(data) => {
			// Authors due date is required when authors are selected
			if (data.formAuthors.length > 0) {
				return data.authorsDueDate !== undefined;
			}
			return true;
		},
		{
			message: "Authors Due Date is required when authors are selected",
			path: ["authorsDueDate"],
		}
	)
	.refine(
		(data) => {
			// Signers due date is required when signers are selected
			if (data.formSigners.length > 0) {
				return data.signersDueDate !== undefined;
			}
			return true;
		},
		{
			message: "Signers Due Date is required when signers are selected",
			path: ["signersDueDate"],
		}
	)
	.refine(
		(data) => {
			// For fixed dates, authors due date must be before or equal to signers due date
			if (
				!data.isRelativeDates &&
				data.formSigners.length > 0 &&
				data.authorsDueDate instanceof Date &&
				data.signersDueDate instanceof Date
			) {
				return data.authorsDueDate <= data.signersDueDate;
			}
			return true;
		},
		{
			message: "Authors Due Date must be before or equal to Signers Due Date",
			path: ["signersDueDate"],
		}
	);

/**
 * Schema for 360 Review Form
 * Used for 360 feedback forms
 *
 * Note: 360 forms default to relative dates.
 * For relative dates, "Days from Date Form Added" is used.
 * For static dates, validation is based on review cycle start date.
 */
export const form360Schema = z
	.object({
		formType: z.literal("360"),
		formName: z.string().min(1, "Form name is required").max(250, "Form name cannot exceed 250 characters"),
		whoCanAddForm: z.enum(["Any Employee", "Subject", "Manager", "Admin"]),
		formAuthor: z.enum(["Any Employee", "Subject"]).optional(), // Only shown when whoCanAddForm is "Subject"
		approval: z.enum([
			"No Approval Needed",
			"Require Approval from Admins",
			"Require Approval from Admins or Managers",
		]),
		formSigners: z.array(employeeValueSchema),
		authorsDueDate: z.union([z.date(), z.number()]).optional(),
		signersDueDate: z.union([z.date(), z.number()]).optional(),
		isRelativeDates: z.boolean().default(true), // Default to relative dates for 360 forms
		dueDatesToggle: z.boolean().default(false), // Checkbox to toggle between relative and fixed dates
		makeVisibleWhenFinished: z.boolean().default(false),
		allow360RequestMessage: z.boolean().default(false),
		redactAuthors: z.boolean().default(false),
		makeFormInactive: z.boolean().default(false),
	})
	.refine(
		(data) => {
			// Form Author is required when "Who Can Add This Form" is "Subject"
			if (data.whoCanAddForm === "Subject") {
				return data.formAuthor !== undefined;
			}
			return true;
		},
		{
			message: "Form Author is required when 'Who Can Add This Form' is 'Subject'",
			path: ["formAuthor"],
		}
	)
	.refine(
		(data) => {
			// Authors due date is required when signers are selected
			if (data.formSigners.length > 0) {
				return data.authorsDueDate !== undefined;
			}
			return true;
		},
		{
			message: "Authors Due Date is required when signers are selected",
			path: ["authorsDueDate"],
		}
	)
	.refine(
		(data) => {
			// Signers due date is required when signers are selected
			if (data.formSigners.length > 0) {
				return data.signersDueDate !== undefined;
			}
			return true;
		},
		{
			message: "Signers Due Date is required when signers are selected",
			path: ["signersDueDate"],
		}
	)
	.refine(
		(data) => {
			// For fixed dates (dueDatesToggle checked), authors due date must be before or equal to signers due date
			if (
				data.dueDatesToggle &&
				data.formSigners.length > 0 &&
				data.authorsDueDate instanceof Date &&
				data.signersDueDate instanceof Date
			) {
				return data.authorsDueDate <= data.signersDueDate;
			}
			return true;
		},
		{
			message: "Authors Due Date must be before or equal to Signers Due Date",
			path: ["signersDueDate"],
		}
	);

/**
 * Schema for External Review Form
 * All fields are optional except formType and formName
 *
 * Character limits:
 * - reviewerName: 1000 characters
 * - reviewerEmail: 1000 characters
 * - emailMessage: 2000 characters
 */
export const externalFormSchema = z.object({
	formType: z.literal("external"),
	formName: z.string().min(1, "Form name is required").max(250, "Form name cannot exceed 250 characters"),
	reviewerName: z.string().max(1000, "Reviewer name cannot exceed 1000 characters").optional(),
	reviewerEmail: z.string().max(1000, "Reviewer email cannot exceed 1000 characters").optional(),
	emailMessage: z.string().max(2000, "Email message cannot exceed 2000 characters").optional(),
	makeVisibleWhenFinished: z.boolean().default(false),
	redactAuthors: z.boolean().default(false),
});

/**
 * Discriminated union of all form types
 */
export const formSelectionSchema = z.discriminatedUnion("formType", [
	standardFormSchema,
	form360Schema,
	externalFormSchema,
]);

export type StandardFormData = z.infer<typeof standardFormSchema>;
export type Form360Data = z.infer<typeof form360Schema>;
export type ExternalFormData = z.infer<typeof externalFormSchema>;
export type FormSelectionData = z.infer<typeof formSelectionSchema>;
